import React, { useEffect } from 'react';
import Footer from '../components/Footer';
import { useScripts } from '../hooks/useScripts';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { getDomain } from '../utils/getDomain';
import {
  BILLING_DESCRIPTOR,
  brandNameFromDomain,
  COMPANY_ADDRESS,
  COMPANY_ADDRESS_KIND,
  COMPANY_ENTITY_TYPE,
  COMPANY_JURISDICTION,
  COMPANY_NAME,
  COMPANY_UBI,
  companyDbaLine,
} from '../constants/company';

function PrivacyPolicy() {
  const domain = getDomain();
  const brandName = brandNameFromDomain(domain);
  const contactEmail = `contact@${domain}`;

  useDocumentTitle('Privacy Policy');
  useScripts();

  useEffect(() => {
    document.body.classList.add('login-form');
    return () => {
      document.body.classList.remove('login-form');
    };
  }, []);

  useEffect(() => {
    const triggerAnimations = () => {
      window.dispatchEvent(new Event('scroll'));
      setTimeout(() => {
        const animatedElements = document.querySelectorAll('.animated');
        animatedElements.forEach((el) => {
          const rect = el.getBoundingClientRect();
          const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
          if (isVisible) {
            const originalClasses = el.className;
            el.className = originalClasses.replace(/\banimated\b/g, '').trim();
            void el.offsetHeight;
            setTimeout(() => {
              el.className = originalClasses;
            }, 10);
          }
        });
      }, 100);
    };

    triggerAnimations();
    setTimeout(triggerAnimations, 200);
    setTimeout(triggerAnimations, 500);
  }, []);

  return (
    <>
      <section className="bg-form legal-doc animatedParent">
        <div className="container">
          <h1 className="legal-doc__title">Privacy Policy</h1>
          <div className="legal-doc__inner">
            <p>
              This Privacy Policy describes how {companyDbaLine(domain)} (“we”, “us”) at {domain}
              handles information. Last updated: October 5, 2026.
              {COMPANY_NAME} is {COMPANY_ENTITY_TYPE}. The contracting and registered office is {COMPANY_ADDRESS}, in {COMPANY_JURISDICTION}.
              {COMPANY_UBI ? ` Washington UBI ${COMPANY_UBI}.` : ''}
            </p>
            <p>
              We draw a hard line between <strong>VPN traffic</strong> and <strong>website or account operations</strong>.
              Our no-logs claims apply to VPN traffic only. They do not mean we collect nothing on this website,
              and they do not mean we never use cookies, payment processors, or account records.
            </p>

            <h3>Consent</h3>
            <p>
              By using our website or VPN service, you consent to this Privacy Policy. If you do not agree,
              do not use the service.
            </p>

            <h3>VPN no-logs policy</h3>
            <p>
              When you are connected to the VPN, we do not log:
            </p>
            <ul>
              <li>Browsing history, destinations, or content of VPN traffic</li>
              <li>DNS queries made through the VPN</li>
              <li>VPN connection timestamps or session duration</li>
              <li>The originating IP address of a VPN session, or IPs assigned through the VPN, as connection logs</li>
            </ul>
            <p>
              We do not sell VPN traffic data, and we do not use VPN traffic for advertising or analytics.
              If we are asked for VPN browsing records we do not have, we cannot produce them.
            </p>

            <h3>Website, account, and payment information we collect</h3>
            <p>
              Operating the website, checkout, and your account is separate from VPN traffic. In those contexts we may collect:
            </p>
            <ul>
              <li>Account email address and password (stored in hashed or equivalent protected form where applicable)</li>
              <li>Billing name, country, region, and postal code needed to process payment</li>
              <li>Payment details handled by our payment processor; we do not store full card numbers after a transaction is submitted</li>
              <li>Plan purchased, purchase amount, transaction identifiers, and the period of VPN access you paid for</li>
              <li>Messages you send us, including support requests</li>
              <li>Affiliate or campaign identifiers stored in your browser if you arrive from a partner link</li>
            </ul>
            <p>
              If you contact us, we receive whatever you choose to include (such as name, email, and the content of the message).
            </p>

            <h3>How we use website and account information</h3>
            <p>We use this information to:</p>
            <ul>
              <li>Provide, operate, and maintain the website and VPN account</li>
              <li>Process payment, prevent fraud, and fulfill digital delivery of credentials</li>
              <li>Send service emails (purchase confirmation, credentials, expiration notices, password reset)</li>
              <li>Respond to support requests</li>
              <li>Debug failures and improve the website and checkout</li>
              <li>Send occasional product or promotional emails about this service, where permitted by law. You can opt out by emailing {contactEmail}</li>
            </ul>
            <p>
              We do not use VPN traffic for marketing. Card statements may show the charge as {BILLING_DESCRIPTOR} or a similar descriptor.
            </p>

            <h3>Website logs and cookies</h3>
            <p>
              The website and related applications keep limited operational logs (for example access logs and error logs)
              so we can secure the site, debug failures, and process checkout. Those logs may include IP address,
              browser or device information, timestamps of website requests, and requested URLs. They are not VPN traffic logs
              and are not a record of what you do while connected to the VPN.
            </p>
            <p>
              We use cookies and similar storage for preferences, checkout, and affiliate attribution.
              You can block cookies in your browser; some site features may not work if you do.
            </p>
            <p>
              Checkout may look up an approximate location from your connection so we can pre-fill country and reduce fraud.
              Payment processing also receives the information required to complete the charge.
            </p>

            <h3>Third parties</h3>
            <p>
              We share website and account information with service providers only as needed to run the business,
              including payment processing, email delivery, and website hosting.
              Those providers process data under their own terms. This Privacy Policy does not cover other websites
              you visit, including sites you reach while using the VPN.
            </p>
            <p>
              We do not sell your personal information. We disclose information if required by a court of competent
              jurisdiction or as needed to prevent fraud or abuse of the service.
            </p>

            <h3>CCPA Privacy Rights (Do Not Sell My Personal Information)</h3>
            <p>Under the CCPA, among other rights, California consumers have the right to:</p>
            <p>Request that a business that collects a consumer's personal data disclose the categories and specific pieces of personal data that a business has collected about consumers.</p>
            <p>Request that a business delete any personal data about the consumer that a business has collected.</p>
            <p>Request that a business that sells a consumer's personal data, not sell the consumer's personal data.</p>
            <p>
              If you make a request, we have one month to respond. To exercise these rights, email {contactEmail}.
            </p>

            <h3>GDPR Data Protection Rights</h3>
            <p>Where GDPR applies, you may have the right to access, rectify, erase, restrict, or object to processing of your personal data, and to data portability, subject to the conditions in that law.</p>
            <p>
              If you make a request, we have one month to respond. Email {contactEmail}.
            </p>

            <h3>Children's Information</h3>
            <p>
              The Services are intended for adults aged 18 and above. If you believe a child has provided information to us,
              email {contactEmail} immediately.
            </p>

            <h3>Contact</h3>
            <p>
              {COMPANY_NAME}<br />
              d/b/a {brandName}<br />
              {COMPANY_ADDRESS_KIND}: {COMPANY_ADDRESS}<br />
              {COMPANY_UBI ? <>Washington UBI {COMPANY_UBI}<br /></> : null}
              <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}

export default PrivacyPolicy;
